import { useState, useEffect, useMemo, useCallback } from 'react'
import { useUser } from '@clerk/clerk-react'
import { useAuthFetch } from '../lib/useAuthFetch'
import { Card, Pill, SectionLabel } from '../components/ui'

const API = import.meta.env.VITE_API_URL ?? ''

// ─── Types (mirror GET /api/v1/locations) ────────────────────────────────────
interface LocationRow {
  id: string
  name: string
  market_tier: string | null
}

type Role = 'admin' | 'manager' | 'viewer'

interface Member {
  id: string
  name: string
  subtitle: string
  role: Role
  color: string
  initials: string
}

// Market tier → label + accent colour (real DB `market_tier`; heat-map palette).
const TIER_META: Record<string, { label: string; color: string }> = {
  elite: { label: 'Elite', color: 'var(--danger)' },
  hot: { label: 'Hot', color: 'var(--warm)' },
  competitive: { label: 'Competitive', color: 'var(--accent)' },
  standard: { label: 'Standard', color: 'var(--text-3)' },
}
const tierMeta = (tier: string | null) => {
  const key = (tier ?? 'standard').toLowerCase()
  return TIER_META[key] ?? { label: key.charAt(0).toUpperCase() + key.slice(1), color: 'var(--text-3)' }
}

const ROLE_LABELS: Record<Role, string> = { admin: 'Admin', manager: 'Manager', viewer: 'Viewer' }
const ROLE_TONE: Record<Role, 'danger' | 'warning' | 'neutral'> = {
  admin: 'danger',
  manager: 'warning',
  viewer: 'neutral',
}

const initialsOf = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('') || '—'

const CheckSVG = ({ size = 11 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3} style={{ width: size, height: size }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

export default function LocationAccess() {
  const authFetch = useAuthFetch()
  const { user, isLoaded: userLoaded } = useUser()

  const [locations, setLocations] = useState<LocationRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<{ msg: string; color: string } | null>(null)

  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<Role>('manager')

  const showToast = useCallback((msg: string, color = 'var(--accent)') => {
    setToast({ msg, color })
    setTimeout(() => setToast(null), 2600)
  }, [])

  // ── Real members: this Clerk instance runs personal-account orgs (the Orgs
  // feature is off), so the org is single-user. We show the signed-in owner as
  // the sole Admin — never invented teammates. If Clerk ever exposes real org
  // memberships, this array is where they'd map in.
  const members = useMemo<Member[]>(() => {
    if (!user) return []
    const name =
      user.fullName ||
      [user.firstName, user.lastName].filter(Boolean).join(' ') ||
      user.username ||
      user.primaryEmailAddress?.emailAddress ||
      'Account owner'
    const email = user.primaryEmailAddress?.emailAddress ?? ''
    return [
      {
        id: user.id,
        name,
        subtitle: email || 'Organization owner',
        role: 'admin',
        color: 'var(--accent)',
        initials: initialsOf(name),
      },
    ]
  }, [user])

  // ── Access matrix — UI state only. Admins can never be restricted, so every
  // grant defaults ON. There is no per-member access table yet, so toggles are
  // local presentation (kept honest via the callout + Save copy below).
  const [access, setAccess] = useState<Record<string, Set<string>>>({})

  useEffect(() => {
    authFetch(`${API}/api/v1/locations`)
      .then(async (r) => {
        if (!r.ok) {
          const d = await r.json().catch(() => ({}))
          throw new Error(d.error || `HTTP ${r.status}`)
        }
        return r.json()
      })
      .then((d: { locations?: LocationRow[] }) => setLocations(d.locations ?? []))
      .catch((e) => setError(e?.message || 'Could not load your locations.'))
      .finally(() => setLoading(false))
  }, [authFetch])

  // Seed access once members + locations are known (everyone granted by default).
  useEffect(() => {
    if (!members.length || !locations.length) return
    setAccess((prev) => {
      if (Object.keys(prev).length) return prev
      const seed: Record<string, Set<string>> = {}
      members.forEach((m) => {
        seed[m.id] = new Set(locations.map((l) => l.id))
      })
      return seed
    })
  }, [members, locations])

  const toggleAccess = (member: Member, loc: LocationRow) => {
    if (member.role === 'admin') return
    setAccess((prev) => {
      const next = { ...prev }
      const set = new Set(next[member.id] ?? [])
      const on = !set.has(loc.id)
      if (on) set.add(loc.id)
      else set.delete(loc.id)
      next[member.id] = set
      showToast(
        `${on ? 'Granted' : 'Removed'}: ${member.name} — ${loc.name}`,
        on ? 'var(--accent)' : 'var(--danger)'
      )
      return next
    })
  }

  const sendInvite = () => {
    if (!inviteEmail || !inviteEmail.includes('@')) {
      showToast('Enter a valid email address', 'var(--warm)')
      return
    }
    // Team seats aren't provisioned on this workspace (personal-account org),
    // so we don't fabricate a "sent" invite.
    showToast('Team seats aren’t enabled on this workspace yet', 'var(--warm)')
  }

  const isSingleUser = members.length <= 1
  const pageLoading = loading || !userLoaded

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'var(--bg)',
        fontFamily: 'var(--sans)',
        color: 'var(--text-1)',
        fontSize: 14,
        overflow: 'hidden',
      }}
    >
      {/* TOPBAR */}
      <div
        style={{
          padding: '12px 24px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexShrink: 0,
          background: 'var(--surface)',
        }}
      >
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em' }}>Location Access Control</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase' }}>
            Admin · Who sees what
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {/* Save exists only where there are per-member toggles to persist. In the
              single-owner state nothing is editable, so we don't show a dead action. */}
          {!isSingleUser && (
            <button
              onClick={() => showToast('Access shown here is local until team seats ship', 'var(--warm)')}
              style={btnStyle(false)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 12, height: 12 }}>
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Save changes
            </button>
          )}
          <button
            onClick={() => document.getElementById('invite-email')?.focus()}
            style={btnStyle(true)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 12, height: 12 }}>
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Invite member
          </button>
        </div>
      </div>

      {/* SCROLL CONTENT */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', minHeight: 0 }}>
        {/* INFO CALLOUT — honest about what this screen does today */}
        <div
          style={{
            background: 'var(--accent-soft)',
            border: '1px solid rgba(11,184,184,0.2)',
            borderRadius: 'var(--r-sm)',
            padding: '11px 14px',
            display: 'flex',
            gap: 9,
            alignItems: 'flex-start',
            marginBottom: 16,
          }}
        >
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <div style={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.5 }}>
            <strong>Admin access:</strong> Admins see every location and can&rsquo;t be restricted. Managers and Viewers
            only see the locations you grant them. Your workspace currently runs on a single owner seat — invite
            teammates to start scoping access per location.
          </div>
        </div>

        {pageLoading ? (
          <Card style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
            Loading your team and locations&hellip;
          </Card>
        ) : error ? (
          <Card style={{ padding: '32px 20px', textAlign: 'center' }}>
            <div style={{ color: 'var(--danger)', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Couldn&rsquo;t load location access
            </div>
            <div style={{ color: 'var(--text-3)', fontSize: 12 }}>{error}</div>
          </Card>
        ) : (
          <>
            {/* ACCESS MATRIX */}
            <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
              <div
                style={{
                  padding: '13px 16px',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  background: 'var(--surface-2)',
                }}
              >
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth={2}>
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <SectionLabel style={{ color: 'var(--text-1)', fontSize: 12, fontWeight: 700, letterSpacing: 0, textTransform: 'none' }}>
                  Team Access
                </SectionLabel>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginLeft: 'auto' }}>
                  {members.length} member{members.length === 1 ? '' : 's'} · {locations.length} location
                  {locations.length === 1 ? '' : 's'}
                </div>
              </div>

              {locations.length === 0 ? (
                <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--text-3)', fontSize: 12 }}>
                  No locations yet. Add a location to start scoping team access.
                </div>
              ) : members.length === 0 ? (
                <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--text-3)', fontSize: 12 }}>
                  No team members found for this workspace.
                </div>
              ) : isSingleUser ? (
                // Single-owner workspace (Clerk Orgs disabled): a one-row matrix of
                // admin-locked cells reads as "denied" and can't be managed. State the
                // truth plainly instead — the owner has full, unrestricted access.
                (() => {
                  const owner = members[0]
                  const showSub = owner.subtitle && owner.subtitle !== owner.name
                  return (
                    <div style={{ padding: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 14 }}>
                        <div
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: 8,
                            background: owner.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 12,
                            fontWeight: 700,
                            color: '#fff',
                            flexShrink: 0,
                          }}
                        >
                          {owner.initials}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-1)' }}>{owner.name}</div>
                          {showSub && (
                            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>
                              {owner.subtitle}
                            </div>
                          )}
                        </div>
                        <Pill tone={ROLE_TONE[owner.role]} variant="chip" style={{ marginLeft: 'auto' }}>
                          Admin · Full access
                        </Pill>
                      </div>

                      <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5, marginBottom: 12 }}>
                        You have full access to all {locations.length} location{locations.length === 1 ? '' : 's'}.
                        As the account owner you can&rsquo;t be restricted — invite teammates below to scope access per
                        location.
                      </div>

                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {locations.map((loc) => {
                          const t = tierMeta(loc.market_tier)
                          return (
                            <span
                              key={loc.id}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '5px 10px',
                                borderRadius: 'var(--r-sm)',
                                border: '1px solid var(--border)',
                                background: 'var(--surface-2)',
                                fontSize: 11.5,
                                color: 'var(--text-1)',
                              }}
                            >
                              <span
                                style={{ width: 6, height: 6, borderRadius: '50%', background: t.color, flexShrink: 0 }}
                                title={t.label}
                              />
                              {loc.name}
                            </span>
                          )
                        })}
                      </div>
                    </div>
                  )
                })()
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ ...thBase, textAlign: 'left', minWidth: 200 }}>Member</th>
                        {locations.map((loc) => {
                          const t = tierMeta(loc.market_tier)
                          return (
                            <th key={loc.id} style={{ ...thBase, textAlign: 'center', minWidth: 96, whiteSpace: 'nowrap' }}>
                              <div style={{ fontSize: 8, color: t.color, marginBottom: 2 }}>{t.label}</div>
                              {loc.name}
                            </th>
                          )
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((m) => {
                        const granted = access[m.id]
                        return (
                          <tr key={m.id}>
                            <td style={tdBase}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                                <div
                                  style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 7,
                                    background: m.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: '#fff',
                                    flexShrink: 0,
                                  }}
                                >
                                  {m.initials}
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-1)' }}>{m.name}</div>
                                  <div
                                    style={{
                                      fontFamily: 'var(--mono)',
                                      fontSize: 9,
                                      color: 'var(--text-3)',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: 140,
                                    }}
                                  >
                                    {m.subtitle}
                                  </div>
                                </div>
                                <Pill tone={ROLE_TONE[m.role]} variant="chip" style={{ marginLeft: 6 }}>
                                  {ROLE_LABELS[m.role]}
                                </Pill>
                              </div>
                            </td>
                            {locations.map((loc) => {
                              const isAdmin = m.role === 'admin'
                              const isOn = isAdmin || !!granted?.has(loc.id)
                              return (
                                <td key={loc.id} style={{ ...tdBase, textAlign: 'center' }}>
                                  <div
                                    onClick={() => toggleAccess(m, loc)}
                                    title={isAdmin ? 'Admins always see every location' : undefined}
                                    style={{
                                      width: 20,
                                      height: 20,
                                      borderRadius: 5,
                                      border: isAdmin ? '2px solid var(--danger)' : '2px solid var(--border-2)',
                                      background: isAdmin
                                        ? 'var(--danger-soft)'
                                        : isOn
                                        ? 'var(--accent)'
                                        : 'var(--surface-3)',
                                      cursor: isAdmin ? 'not-allowed' : 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      margin: '0 auto',
                                      transition: 'all 0.15s',
                                    }}
                                  >
                                    {isOn && <CheckSVG />}
                                  </div>
                                </td>
                              )
                            })}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* INVITE CARD */}
            <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
              <div
                style={{
                  padding: '13px 16px',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  background: 'var(--surface-2)',
                }}
              >
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth={2}>
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <SectionLabel style={{ color: 'var(--text-1)', fontSize: 12, fontWeight: 700, letterSpacing: 0, textTransform: 'none' }}>
                  Invite Team Member
                </SectionLabel>
              </div>
              <div style={{ display: 'flex', gap: 8, padding: '14px 16px', alignItems: 'center' }}>
                <input
                  id="invite-email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@yourcompany.com"
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: '1.5px solid var(--border-2)',
                    borderRadius: 'var(--r-sm)',
                    background: 'var(--surface-2)',
                    color: 'var(--text-1)',
                    fontFamily: 'var(--sans)',
                    fontSize: 12,
                    outline: 'none',
                  }}
                />
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as Role)}
                  style={{
                    padding: '7px 10px',
                    border: '1.5px solid var(--border-2)',
                    borderRadius: 'var(--r-sm)',
                    background: 'var(--surface-2)',
                    color: 'var(--text-1)',
                    fontFamily: 'var(--sans)',
                    fontSize: 11,
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <option value="viewer">Viewer</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
                <button onClick={sendInvite} style={btnStyle(true)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 12, height: 12 }}>
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                  Send invite
                </button>
              </div>
              {/* ROLE LEGEND */}
              <div style={{ padding: '0 16px 14px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {[
                  { color: 'var(--danger)', label: 'Admin — full access, all locations, can manage team' },
                  { color: 'var(--warm)', label: 'Manager — granted locations, can manage rival tracking' },
                  { color: 'var(--text-3)', label: 'Viewer — granted locations, read-only' },
                ].map((item) => (
                  <div
                    key={item.label}
                    style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: item.color, display: 'inline-block' }} />
                    {item.label}
                  </div>
                ))}
              </div>
            </Card>
          </>
        )}
      </div>

      {/* TOAST */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: 'var(--surface)',
            border: '1px solid var(--border-2)',
            borderRadius: 'var(--r-sm)',
            padding: '11px 18px',
            fontSize: 12,
            fontWeight: 600,
            boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
            zIndex: 9000,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: toast.color, flexShrink: 0, display: 'inline-block' }} />
          {toast.msg}
        </div>
      )}
    </div>
  )
}

// ─── Shared inline styles ────────────────────────────────────────────────────
const thBase: React.CSSProperties = {
  padding: '10px 14px',
  fontFamily: 'var(--mono)',
  fontSize: 9,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--text-3)',
  borderBottom: '2px solid var(--border-2)',
  background: 'var(--surface-2)',
}
const tdBase: React.CSSProperties = {
  padding: '10px 14px',
  borderBottom: '1px solid var(--border)',
  verticalAlign: 'middle',
}
const btnStyle = (accent: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '7px 13px',
  borderRadius: 'var(--r-sm)',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'var(--sans)',
  border: `1.5px solid ${accent ? 'var(--accent)' : 'var(--border-2)'}`,
  background: accent ? 'var(--accent)' : 'var(--surface-3)',
  color: accent ? '#fff' : 'var(--text-1)',
})
